// Wither (M20.1). PURE (ENG-1): the summon-frame pattern a player builds to spawn
// the boss, and the boss's 3-phase state machine — a charging-up spawn window
// where it is invulnerable, a phase-1 airborne skull barrage, and a faster,
// tougher phase 2 once it drops below half health.
import { WITHER } from '../config';

type BlockAt = (x: number, y: number, z: number) => string;

/** Soul blocks the summon frame is built from (soul sand or soul soil). */
export function isSoulBlock(block: string): boolean {
  return block === 'soul_sand' || block === 'soul_soil';
}

export interface WitherBuild {
  /** Frame cells (skulls + soul blocks) to clear to air on ignition. */
  clear: Array<[number, number, number]>;
  /** Feet block position of the spawned wither (caller centres with +0.5). */
  spawn: [number, number, number];
}

/**
 * Test whether the wither_skeleton_skull at (x,y,z) is the CENTRE of a completed
 * summon frame: three skulls in a horizontal row (x or z axis), each on a soul
 * block, with a soul block one deeper below the centre (the MC T-shape). Returns
 * the frame to clear + the spawn feet, or null. The host calls this for the placed
 * skull and its two horizontal neighbours so any of the three completes it.
 */
export function checkWitherSummon(at: BlockAt, x: number, y: number, z: number): WitherBuild | null {
  if (at(x, y, z) !== 'wither_skeleton_skull') return null;
  for (const [ax, az] of [[1, 0], [0, 1]] as const) {
    const lx = x - ax, lz = z - az, rx = x + ax, rz = z + az;
    // Three skulls in a row.
    if (at(lx, y, lz) !== 'wither_skeleton_skull' || at(rx, y, rz) !== 'wither_skeleton_skull') continue;
    // Each skull sits on a soul block, and one more soul block hangs below the centre.
    if (!isSoulBlock(at(x, y - 1, z)) || !isSoulBlock(at(lx, y - 1, lz)) || !isSoulBlock(at(rx, y - 1, rz))) continue;
    if (!isSoulBlock(at(x, y - 2, z))) continue;
    return {
      clear: [
        [x, y, z], [lx, y, lz], [rx, y, rz],
        [x, y - 1, z], [lx, y - 1, lz], [rx, y - 1, rz],
        [x, y - 2, z],
      ],
      // Wither is 3 tall; drop its feet so its body fills the frame's centre column.
      spawn: [x, y - 1, z],
    };
  }
  return null;
}

export type WitherPhase = 'summon' | 'phase1' | 'phase2';

/** Which phase the wither is in, from its remaining charge-up window and health. */
export function witherPhase(health: number, maxHealth: number, summonTicksLeft: number): WitherPhase {
  if (summonTicksLeft > 0) return 'summon';
  return health <= maxHealth * WITHER.PHASE2_HEALTH_FRACTION ? 'phase2' : 'phase1';
}

/** The wither shrugs off all damage while it charges up on spawn. */
export function witherInvulnerable(summonTicksLeft: number): boolean {
  return summonTicksLeft > 0;
}

/** Ticks between skull shots for a phase (phase 2 fires markedly faster). */
export function witherSkullInterval(phase: WitherPhase): number {
  return phase === 'phase2' ? WITHER.SKULL_INTERVAL_PHASE2 : WITHER.SKULL_INTERVAL_PHASE1;
}
